import {
  Button,
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  DrawerProps,
  Grid,
  HStack,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
  Link,
} from "@chakra-ui/react";
import React, { useMemo } from "react";

import ProductSection from "../ProductSection";

import useAuth from "../../useAuth";
import { fetchAllBrands } from "../../../queries/fetchAllBrands";
import {
  brandsToExclude,
  categoriesToExclude,
} from "../../../queries/toExclude";
import { fetchAllCategoriesWithBrands } from "../../../queries/fetchAllCategoriesWithBrands";
import { parseHeaderBrands } from "../../utils/shopUtils";

const MobileProductsDrawer = (props: Omit<DrawerProps, "children">) => {
  const products = fetchAllCategoriesWithBrands();

  const brands = fetchAllBrands();
  const { loggedIn, isUnlicensedUser, isCustomerDefault, isBankAcc } =
    useAuth();
  // CATS TO EXCLUDE
  const relatedProductCategoryName = "Related Product";
  const filteredCategories = useMemo(() => {
    const categories = new Map();

    products.forEach((product: any) => {
      product.productCategories.nodes.forEach((cat: any) => {
        if (!categories.has(cat.id)) {
          categories.set(cat.id, { ...cat, productIds: new Set([product.id]) });
        } else {
          categories.get(cat.id).productIds.add(product.id);
        }
      });
    });

    const categoryArray = Array.from(categories.values());

    return categoryArray.filter((cat) => {
      const shouldInclude =
        isCustomerDefault || isUnlicensedUser || !loggedIn || isBankAcc
          ? !categoriesToExclude.includes(cat.name) &&
            cat.name !== relatedProductCategoryName &&
            cat.name !== "Best Selling Product"
          : cat.name !== "Best Selling Product" &&
            cat.name !== relatedProductCategoryName;

      return shouldInclude;
    });
  }, [
    isCustomerDefault,
    isUnlicensedUser,
    isBankAcc,
    products,
    categoriesToExclude,
    relatedProductCategoryName,
  ]);

  // CATS TO EXCLUDE -END

  const parsedCategories = useMemo(() => {
    const shouldFilterBrands =
      isUnlicensedUser || !loggedIn || isCustomerDefault || isBankAcc;

    const categoriesWithDetails = filteredCategories
      .map((category) => {
        // Fetch products in this category
        const productsInCategory = Array.from(category.productIds)
          .map((id) => products.find((product: any) => product.id === id))
          .filter((product) => product != null); // Exclude undefined products

        // Collect unique brands, excluding specific brands based on user status and globally excluded names
        const brands = new Map();
        productsInCategory.forEach((product) => {
          product.productTags.nodes.forEach((tag: any) => {
            if (
              !brands.has(tag.slug) &&
              !(shouldFilterBrands && brandsToExclude.includes(tag.name)) &&
              tag.name !== "Medical Devices" &&
              tag.name !== "Pharmaceuticals"
            ) {
              brands.set(tag.slug, tag);
            }
          });
        });

        // Convert Map to array and sort alphabetically by brand name
        const sortedBrands = Array.from(brands.values()).sort((a, b) =>
          a.name.localeCompare(b.name),
        );

        // Include category only if it has valid brands
        if (sortedBrands.length > 0) {
          return {
            ...category,
            brands: sortedBrands,
          };
        }
        return null; // Return null for categories with no brands, will filter out later
      })
      .filter((category) => category != null); // Remove null categories

    // Sort categories alphabetically by their names
    return categoriesWithDetails.sort((a, b) => a.name.localeCompare(b.name));
  }, [
    filteredCategories,
    products,
    isUnlicensedUser,
    loggedIn,
    isCustomerDefault,
    isBankAcc,
    brandsToExclude,
  ]);

  const filteredBrands = React.useMemo(() => {
    if (isUnlicensedUser || !loggedIn || isCustomerDefault || isBankAcc) {
      return brands.filter((item: any) => {
        return !brandsToExclude.includes(item.name);
      });
    }

    return brands.filter((item: any) => {
      return (
        item.name !== "Unlicensed" &&
        item.name !== "Medical Devices" &&
        item.name !== "Pharmaceuticals"
      );
    });
  }, [isUnlicensedUser, loggedIn, isCustomerDefault, isBankAcc, brands]);

  const parsedHeaderBrands = useMemo(() => {
    return parseHeaderBrands(filteredBrands);
  }, [filteredBrands]);

  return (
    <Drawer placement="bottom" {...props}>
      <DrawerOverlay />
      <DrawerContent
        overflowY="scroll"
        justifyContent="space-between"
        h="100%"
        bg="#f3f6f4"
        w="100%"
      >
        <Tabs
          colorScheme="unset"
          isLazy
          w="100%"
          bg="#f3f6f4"
          variant="soft-rounded"
          id="1"
        >
          <HStack p="20px" h="90px" justifyContent="space-between">
            <DrawerCloseButton
              top={8}
              left={2}
              position="absolute"
              color="#000"
            />
            <HStack ml="30px">
              <TabList>
                <Tab
                  fontSize="18px"
                  color="#1A202C"
                  _selected={{ bg: "#3B2060", color: "#fff" }}
                  width="132px"
                  h="52px"
                >
                  Categories
                </Tab>
                <Tab
                  fontSize="18px"
                  color="#1A202C"
                  _selected={{ bg: "#3B2060", color: "#fff" }}
                  width="132px"
                  h="52px"
                >
                  Brands
                </Tab>
              </TabList>
            </HStack>
          </HStack>

          <TabPanels>
            <TabPanel>
              <Link href="https://www.medsupplysolutions.com/shop/">
                <Button
                  fontSize="14px"
                  _hover={{ background: "none" }}
                  variant="unstyled"
                  color="#BB5D82"
                  fontWeight="700"
                  pb={2}
                >
                  All Products
                </Button>
              </Link>
              {parsedCategories?.map((item, index) => {
                return (
                  <ProductSection
                    key={index}
                    categoryName={item.name}
                    brands={item.brands}
                    categorySlug={item.slug}
                  />
                );
              })}
            </TabPanel>
            <Stack
              w="100%"
              h={{ xl: "550px", "3xl": "730px", "4xl": "1044px" }}
            >
              <TabPanel w="100%" h="100vh">
                <Text fontSize="14px" color="#4A5568" pb="16px">
                  Brands
                </Text>
                <Grid
                  pb="20px"
                  templateColumns={{
                    base: "repeat(1, 1fr)",
                    sm: "repeat(1, 1fr)",
                    md: "repeat(2, 1fr)",
                    lg: "repeat(3, 1fr)",
                    xl: "repeat(6, 1fr)",
                  }}
                  gap={6}
                >
                  {parsedHeaderBrands?.map((item: any, index: any) => {
                    return (
                      <VStack maxW="235px" key={index} alignItems="flex-start">
                        <Text
                          fontSize="18px"
                          color="#3B2060"
                          fontWeight="700"
                          textAlign="start"
                          pl="4"
                        >
                          {item.startsWith}
                        </Text>

                        <VStack
                          borderTopWidth={1}
                          borderTopColor="#E2E8F0"
                          flex={1}
                          width="100%"
                          pt="6"
                          alignItems="flex-start"
                          pl="4"
                        >
                          {item.items.map((brand: any, index: any) => {
                            return (
                              <Link key={index} href={`/${brand.slug}/`}>
                                <Text
                                  _hover={{ textDecoration: "underline" }}
                                  fontSize="18px"
                                >
                                  {brand.name}
                                </Text>
                              </Link>
                            );
                          })}
                        </VStack>
                      </VStack>
                    );
                  })}
                </Grid>
              </TabPanel>
            </Stack>
          </TabPanels>
        </Tabs>

        {/* <Text>All products</Text>
        {parsedCategories?.map((item, index) => {
          return (
            <ProductSection
              key={index}
              categoryName={item.name}
              brands={item.brands}
              categorySlug={item.slug}
            />
          );
        })} */}
      </DrawerContent>
    </Drawer>
  );
};

export default MobileProductsDrawer;
